/**
 * useProfile Hook
 *
 * React Query hook for fetching user profile data
 */

import { useQuery } from '@tanstack/react-query';
import { profileService } from '../services/profile.service';
import { PROFILE_QUERY_KEYS } from '../constants';
import type { Profile } from '../types';

/**
 * Hook to fetch current user's profile
 *
 * @example
 * ```tsx
 * const { profile, isLoading, error } = useProfile();
 *
 * if (isLoading) return <Skeleton />;
 * if (error) return <Error message={error.message} />;
 *
 * return <div>{profile.full_name}</div>;
 * ```
 */
export function useProfile() {
  const query = useQuery<Profile>({
    queryKey: PROFILE_QUERY_KEYS.profile,
    queryFn: async () => {
      const data = await profileService.getProfile();
      return data as Profile;
    },
    staleTime: 5 * 60 * 1000, // 5 min — profile rarely changes (#2071)
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    refetchOnWindowFocus: false, // profile doesn't change on focus (#2071)
    retry: 1,
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

/**
 * Hook to get profile data with suspense
 * Use this in components wrapped with Suspense boundary
 */
export function useProfileSuspense() {
  const query = useQuery({
    queryKey: PROFILE_QUERY_KEYS.profile,
    queryFn: () => profileService.getProfile(),
    suspense: true,
  });

  return {
    profile: query.data as Profile, // Safe to cast with suspense
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

